import React, { useState, useEffect } from 'react';
import { getQuoteRequestFields } from '../../config/quoteRequestFields';
import './QuoteRequestModal.css';

const QuoteRequestModal = ({ 
  isOpen, 
  onClose, 
  offer = null, 
  categoryId = null, 
  subcategoryId = null,
  agentId = null 
}) => {
  const [formData, setFormData] = useState({
    // Basic Information
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    
    // Travel Details
    travelDates: '',
    flexibleDates: false,
    adults: 1,
    children: 0,
    childrenAges: '',
    
    // Dynamic fields will be added based on destination
    budgetRange: '',
    specialRequests: '',
    additionalNotes: ''
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [dynamicFields, setDynamicFields] = useState(null);

  // Load dynamic fields when modal opens or destination changes
  useEffect(() => {
    if (isOpen && categoryId && subcategoryId) {
      const fields = getQuoteRequestFields(categoryId, subcategoryId);
      setDynamicFields(fields);
      
      // Initialize dynamic field values
      if (fields && fields.fields) {
        const dynamicDefaults = {};
        Object.keys(fields.fields).forEach(fieldKey => {
          const field = fields.fields[fieldKey];
          if (field.type === 'multiselect') {
            dynamicDefaults[fieldKey] = [];
          } else {
            dynamicDefaults[fieldKey] = '';
          }
        });
        
        setFormData(prev => ({
          ...prev,
          ...dynamicDefaults
        }));
      }
    }
  }, [isOpen, categoryId, subcategoryId]);

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        travelDates: '',
        flexibleDates: false,
        adults: 1,
        children: 0,
        childrenAges: '',
        budgetRange: '',
        specialRequests: '',
        additionalNotes: ''
      });
      setErrors({});
      setSubmitSuccess(false);
      setDynamicFields(null);
    }
  }, [isOpen]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  };

  const handleMultiselectChange = (field, option) => {
    setFormData(prev => {
      const currentValues = prev[field] || [];
      const isSelected = currentValues.includes(option.value);
      
      return {
        ...prev,
        [field]: isSelected 
          ? currentValues.filter(v => v !== option.value)
          : [...currentValues, option.value]
      };
    });
  };

  const validateForm = () => {
    const newErrors = {};

    // Basic validation
    if (!formData.firstName.trim()) newErrors.firstName = 'First name is required';
    if (!formData.lastName.trim()) newErrors.lastName = 'Last name is required';
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    if (!formData.travelDates.trim()) newErrors.travelDates = 'Travel dates are required';
    
    // Validate adults/children
    if (formData.adults < 1) newErrors.adults = 'At least 1 adult is required';
    if (formData.children > 0 && !formData.childrenAges.trim()) {
      newErrors.childrenAges = 'Please enter ages of children (e.g., 5, 8, 12)';
    }

    // Validate required dynamic fields
    if (dynamicFields && dynamicFields.fields) {
      Object.keys(dynamicFields.fields).forEach(fieldKey => {
        const field = dynamicFields.fields[fieldKey];
        if (field.required && !formData[fieldKey]) {
          newErrors[fieldKey] = `${field.label} is required`;
        }
      });
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    
    try {
      // Prepare submission data
      const submissionData = {
        ...formData,
        offerId: offer?.id || null,
        categoryId,
        subcategoryId,
        agentId,
        source: 'website',
        userAgent: navigator.userAgent,
        referrer: document.referrer
      };

      // Submit to API
      const response = await fetch('/api/public/quote-requests', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(submissionData)
      });

      if (response.ok) {
        setSubmitSuccess(true);
        // Auto-close after 3 seconds
        setTimeout(() => {
          onClose();
        }, 3000);
      } else {
        throw new Error('Failed to submit quote request');
      }
    } catch (error) {
      console.error('Error submitting quote request:', error);
      setErrors({ submit: 'Failed to submit request. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderDynamicField = (fieldKey, field) => {
    switch (field.type) {
      case 'select':
        return (
          <div key={fieldKey} className="form-group">
            <label htmlFor={fieldKey}>
              {field.label}
              {field.required && <span className="required">*</span>}
            </label>
            <select
              id={fieldKey}
              value={formData[fieldKey] || ''}
              onChange={(e) => handleInputChange(fieldKey, e.target.value)}
              className={errors[fieldKey] ? 'error' : ''}
            >
              {field.options.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            {errors[fieldKey] && <span className="error-text">{errors[fieldKey]}</span>}
          </div>
        );

      case 'multiselect':
        return (
          <div key={fieldKey} className="form-group">
            <label>
              {field.label}
              {field.required && <span className="required">*</span>}
            </label>
            <div className="multiselect-options">
              {field.options.map(option => (
                <label key={option.value} className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={(formData[fieldKey] || []).includes(option.value)}
                    onChange={() => handleMultiselectChange(fieldKey, option)}
                  />
                  <span className="checkmark"></span>
                  {option.label}
                </label>
              ))}
            </div>
            {errors[fieldKey] && <span className="error-text">{errors[fieldKey]}</span>}
          </div>
        );

      case 'text':
        return (
          <div key={fieldKey} className="form-group">
            <label htmlFor={fieldKey}>
              {field.label}
              {field.required && <span className="required">*</span>}
            </label>
            <input
              type="text"
              id={fieldKey}
              value={formData[fieldKey] || ''}
              onChange={(e) => handleInputChange(fieldKey, e.target.value)}
              placeholder={field.placeholder || ''}
              className={errors[fieldKey] ? 'error' : ''}
            />
            {errors[fieldKey] && <span className="error-text">{errors[fieldKey]}</span>}
          </div>
        );

      default:
        return null;
    }
  };

  if (!isOpen) return null;

  return (
    <div className="quote-modal-overlay" onClick={onClose}>
      <div className="quote-modal" onClick={(e) => e.stopPropagation()}>
        <div className="quote-modal-header">
          <h2>Request a Free Quote</h2>
          {offer && <p className="offer-title">for "{offer.title}"</p>}
          {dynamicFields && (
            <p className="destination-name">
              <i className="fas fa-map-marker-alt"></i>
              {dynamicFields.name}
            </p>
          )}
          <button className="close-button" onClick={onClose}>
            <i className="fas fa-times"></i>
          </button>
        </div>

        <div className="quote-modal-body">
          {submitSuccess ? (
            <div className="success-message">
              <div className="success-icon">
                <i className="fas fa-check-circle"></i>
              </div>
              <h3>Quote Request Submitted!</h3>
              <p>Thank you for your interest. We'll get back to you within 24 hours with a personalized quote.</p>
              <p className="auto-close">This window will close automatically...</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="quote-request-form">
              {/* Basic Information Section */}
              <div className="form-section">
                <h3>Contact Information</h3>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="firstName">
                      First Name <span className="required">*</span>
                    </label>
                    <input
                      type="text"
                      id="firstName"
                      value={formData.firstName}
                      onChange={(e) => handleInputChange('firstName', e.target.value)}
                      className={errors.firstName ? 'error' : ''}
                    />
                    {errors.firstName && <span className="error-text">{errors.firstName}</span>}
                  </div>
                  <div className="form-group">
                    <label htmlFor="lastName">
                      Last Name <span className="required">*</span>
                    </label>
                    <input
                      type="text"
                      id="lastName"
                      value={formData.lastName}
                      onChange={(e) => handleInputChange('lastName', e.target.value)}
                      className={errors.lastName ? 'error' : ''}
                    />
                    {errors.lastName && <span className="error-text">{errors.lastName}</span>}
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="email">
                      Email Address <span className="required">*</span>
                    </label>
                    <input
                      type="email"
                      id="email"
                      value={formData.email}
                      onChange={(e) => handleInputChange('email', e.target.value)}
                      className={errors.email ? 'error' : ''}
                    />
                    {errors.email && <span className="error-text">{errors.email}</span>}
                  </div>
                  <div className="form-group">
                    <label htmlFor="phone">Phone Number</label>
                    <input
                      type="tel"
                      id="phone"
                      value={formData.phone}
                      onChange={(e) => handleInputChange('phone', e.target.value)}
                      placeholder="(555) 123-4567"
                    />
                  </div>
                </div>
              </div>

              {/* Travel Details Section */}
              <div className="form-section">
                <h3>Travel Details</h3>
                <div className="form-group">
                  <label htmlFor="travelDates">
                    Travel Dates <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    id="travelDates"
                    value={formData.travelDates}
                    onChange={(e) => handleInputChange('travelDates', e.target.value)}
                    placeholder="e.g., March 15-22, 2024 or Spring 2024"
                    className={errors.travelDates ? 'error' : ''}
                  />
                  {errors.travelDates && <span className="error-text">{errors.travelDates}</span>}
                </div>
                
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="adults">Number of Adults</label>
                    <select
                      id="adults"
                      value={formData.adults}
                      onChange={(e) => handleInputChange('adults', parseInt(e.target.value))}
                      className={errors.adults ? 'error' : ''}
                    >
                      {[1,2,3,4,5,6,7,8].map(num => (
                        <option key={num} value={num}>{num} Adult{num > 1 ? 's' : ''}</option>
                      ))}
                    </select>
                    {errors.adults && <span className="error-text">{errors.adults}</span>}
                  </div>
                  <div className="form-group">
                    <label htmlFor="children">Number of Children (0-17)</label>
                    <select
                      id="children"
                      value={formData.children}
                      onChange={(e) => handleInputChange('children', parseInt(e.target.value))}
                    >
                      {[0,1,2,3,4,5,6].map(num => (
                        <option key={num} value={num}>{num} Child{num !== 1 ? 'ren' : ''}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {formData.children > 0 && (
                  <div className="form-group">
                    <label htmlFor="childrenAges">
                      Children's Ages <span className="required">*</span>
                    </label>
                    <input
                      type="text"
                      id="childrenAges"
                      value={formData.childrenAges}
                      onChange={(e) => handleInputChange('childrenAges', e.target.value)}
                      placeholder="e.g., 5, 8, 12"
                      className={errors.childrenAges ? 'error' : ''}
                    />
                    {errors.childrenAges && <span className="error-text">{errors.childrenAges}</span>}
                  </div>
                )}

                <div className="form-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={formData.flexibleDates}
                      onChange={(e) => handleInputChange('flexibleDates', e.target.checked)}
                    />
                    <span className="checkmark"></span>
                    My travel dates are flexible
                  </label>
                </div>
              </div>

              {/* Dynamic Destination-Specific Fields */}
              {dynamicFields && dynamicFields.fields && (
                <div className="form-section">
                  <h3>{dynamicFields.name} Preferences</h3>
                  {Object.keys(dynamicFields.fields).map(fieldKey => 
                    renderDynamicField(fieldKey, dynamicFields.fields[fieldKey])
                  )}
                </div>
              )}

              {/* Additional Information Section */}
              <div className="form-section">
                <h3>Additional Information</h3>
                <div className="form-group">
                  <label htmlFor="budgetRange">Budget Range (Optional)</label>
                  <select
                    id="budgetRange"
                    value={formData.budgetRange}
                    onChange={(e) => handleInputChange('budgetRange', e.target.value)}
                  >
                    <option value="">No preference</option>
                    <option value="under_1000">Under $1,000</option>
                    <option value="1000_2500">$1,000 - $2,500</option>
                    <option value="2500_5000">$2,500 - $5,000</option>
                    <option value="5000_10000">$5,000 - $10,000</option>
                    <option value="over_10000">Over $10,000</option>
                  </select>
                </div>
                
                <div className="form-group">
                  <label htmlFor="specialRequests">Special Requests</label>
                  <textarea
                    id="specialRequests"
                    value={formData.specialRequests}
                    onChange={(e) => handleInputChange('specialRequests', e.target.value)}
                    placeholder="Accessibility needs, dietary restrictions, celebrations, etc."
                    rows="3"
                  ></textarea>
                </div>

                <div className="form-group">
                  <label htmlFor="additionalNotes">Additional Notes</label>
                  <textarea
                    id="additionalNotes"
                    value={formData.additionalNotes}
                    onChange={(e) => handleInputChange('additionalNotes', e.target.value)}
                    placeholder="Any other details that would help us create the perfect quote for you..."
                    rows="3"
                  ></textarea>
                </div>
              </div>

              {errors.submit && (
                <div className="error-message">
                  <i className="fas fa-exclamation-triangle"></i>
                  {errors.submit}
                </div>
              )}

              <div className="form-actions">
                <button type="button" className="btn-secondary" onClick={onClose}>
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="btn-primary"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <i className="fas fa-spinner fa-spin"></i>
                      Submitting...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-paper-plane"></i>
                      Request Free Quote
                    </>
                  )}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default QuoteRequestModal;
